import {
  currentTutorialAtom,
  currentTutorialStartOptionsAtom,
  currentTutorialStepAtom,
  getCurrentTutorial,
  hideTutorialOverlayAtom,
  postponedSignalsAtom,
  presentTutorialOverlayAtom,
  tutorialTargetRegistrationsAtom,
} from 'app/atoms';
import { useIsFocused } from '@react-navigation/native';
import { images } from 'app/design/images';
import FancySeparator from 'app/design/images/fancy-separator';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';

import { useDeviceWidth } from 'app/hooks/use-device-size';
import { useIsBlockingModalVisible } from 'app/hooks/use-is-blocking-modal-visible';
import { Event, track } from 'app/lib/analytics';
import { isBlockingModalVisible } from 'app/lib/blocking-modals';
import {
  addTutorialTargetPadding,
  areTutorialTargetRectsEqual,
  canPresentTutorialOverlay,
  getFocusedTutorialTarget,
  isTutorialOverlayRouteActive,
  isTutorialTargetInViewport,
  shouldResumeTutorialOverlay,
} from 'app/lib/tutorial-overlay';
import { getCurrentRouteBranchIdentity, navigationRef, subscribeNavigationState } from 'app/lib/root-navigation';
import { useOnePreference, useUpsertOnePreference } from 'app/network/preferences';
import type { Signal } from 'app/network/signals';
import { UserLevel } from 'app/schemas/user';
import { useTutorialDefinitions } from 'app/services/tutorials-definitions';
import type { SpecificNavigationProp } from 'app/types/navigation';
import {
  Tutorial,
  type ShowTutorialOverlayType,
  type StartTutorialOptions,
  type TutorialOverlayProps,
  type TutorialOverlayPadding,
  type TutorialOverlayPlacement,
  type TutorialRef,
  type TutorialTargetRect,
  type TutorialTargetRef,
} from 'app/types/tutorial';
import { delay } from 'app/utils/helpers';
import { isMobileNative } from 'app/utils/web-helpers';
import { Image } from 'expo-image';
import { useAtomValue, useSetAtom, useAtom } from 'jotai';
import { type SetStateAction, useEffect, useEffectEvent, useId, useRef, useState, useSyncExternalStore } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { LayoutChangeEvent, StyleProp, ViewStyle } from 'react-native';
import { View, useWindowDimensions } from 'react-native';
import Animated, {
  cancelAnimation,
  Easing,
  type SharedValue,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

type OverlaySegment = 'top' | 'left' | 'right' | 'bottom';

const LIVE_MEASURE_INTERVAL_MS = 150;
const STABLE_MEASURE_FRAME_COUNT = 2;
const OVERLAY_FADE_DURATION_MS = 220;
const MODAL_ANIMATION_DURATION_MS = 260;
const TARGET_RING_PADDING = 4;
const POINTER_WIDTH = 6;
const POINTER_MAX_LENGTH = 34;
const POINTER_GAP = 4;

function clamp(value: number, min: number, max: number) {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

interface OverlayMargins {
  x: number;
  y: number;
}

interface ViewportBounds {
  bottom: number;
  left: number;
  right: number;
  top: number;
}

interface TutorialOverlayLayout {
  cardWidth: number;
  estimatedModalHeight: number;
  modalGap: number;
  targetLeft: number | undefined;
  targetTop: number | undefined;
  viewportBounds: ViewportBounds;
}

function getTutorialOverlayLayout({
  deviceWidth,
  insets,
  margins,
  modalHeight,
  overlayHeight,
  overlayWidth,
  targetRect,
}: {
  deviceWidth: number;
  insets: { bottom: number; top: number };
  margins: OverlayMargins;
  modalHeight: number;
  overlayHeight: number;
  overlayWidth: number;
  targetRect?: TutorialTargetRect;
}): TutorialOverlayLayout {
  const modalMarginHorizontal =
    deviceWidth <= 360 ? theme.spacing['spacing.general.x4'] : theme.spacing['spacing.general.x6'];
  const edgePadding = theme.spacing['spacing.general.x4'];

  return {
    cardWidth: Math.min(deviceWidth - 2 * modalMarginHorizontal, 420),
    estimatedModalHeight: modalHeight || 168,
    modalGap: theme.spacing['spacing.general.x6'],
    targetLeft: targetRect ? -margins.x + targetRect.x : undefined,
    targetTop: targetRect ? -margins.y + targetRect.y : undefined,
    viewportBounds: {
      left: -margins.x + edgePadding,
      right: -margins.x + overlayWidth - edgePadding,
      top: -margins.y + insets.top + edgePadding,
      bottom: -margins.y + overlayHeight - insets.bottom - edgePadding,
    },
  };
}

function getOverlaySegmentStyle(
  position: OverlaySegment,
  targetRect: TutorialTargetRect | undefined,
  overlayWidth: number,
  overlayHeight: number,
  margins: OverlayMargins,
): StyleProp<ViewStyle> {
  const style: ViewStyle = {
    position: 'absolute',
    backgroundColor: 'rgb(0, 0, 0)',
    zIndex: 10,
    display: targetRect ? 'flex' : 'none',
  };

  if (!targetRect) {
    if (position === 'top') {
      style.display = 'flex';
      style.width = overlayWidth;
      style.height = overlayHeight;
      style.left = -margins.x;
      style.top = -margins.y;
    }
    return style;
  }

  switch (position) {
    case 'top':
      style.width = overlayWidth;
      style.height = Math.max(0, targetRect.y);
      style.left = -margins.x;
      style.top = -margins.y;
      break;
    case 'left':
      style.width = Math.max(0, targetRect.x);
      style.height = Math.max(0, targetRect.height);
      style.left = -margins.x;
      style.top = -margins.y + targetRect.y;
      break;
    case 'right':
      style.width = Math.max(0, overlayWidth - targetRect.x - targetRect.width);
      style.height = Math.max(0, targetRect.height);
      style.left = -margins.x + targetRect.x + targetRect.width;
      style.top = -margins.y + targetRect.y;
      break;
    case 'bottom':
      style.width = overlayWidth;
      style.height = Math.max(0, overlayHeight - targetRect.y - targetRect.height);
      style.left = -margins.x;
      style.top = -margins.y + targetRect.y + targetRect.height;
      break;
  }

  return style;
}

interface ModalPresentation {
  showCompletedModal: Tutorial | 'no';
  showModal: TutorialOverlayPlacement | 'no';
}

function getModalPresentation({
  isRendered,
  layout,
  previous,
  targetRect,
  tutorialOverlayOptions,
}: {
  isRendered: boolean;
  layout: TutorialOverlayLayout;
  previous: ModalPresentation;
  targetRect?: TutorialTargetRect;
  tutorialOverlayOptions?: TutorialOverlayProps;
}): ModalPresentation {
  const presentation: ModalPresentation = { showModal: 'no', showCompletedModal: 'no' };
  if (tutorialOverlayOptions?.text?.includes('.stepFinal.')) {
    presentation.showCompletedModal = tutorialOverlayOptions.tutorial;
    return presentation;
  }
  if (!tutorialOverlayOptions?.text) return presentation;
  if (!tutorialOverlayOptions.pointing) {
    presentation.showModal = 'centered';
    return presentation;
  }
  if (!targetRect) {
    if (isRendered) presentation.showModal = previous.showModal;
    return presentation;
  }
  if (layout.targetTop === undefined) {
    if (isRendered) presentation.showModal = previous.showModal;
    return presentation;
  }

  const spaceAvailAbove = layout.targetTop - layout.viewportBounds.top - layout.modalGap;
  const spaceAvailBelow = layout.viewportBounds.bottom - (layout.targetTop + targetRect.height) - layout.modalGap;
  presentation.showModal = spaceAvailAbove > spaceAvailBelow ? 'above' : 'below';
  return presentation;
}

function getModalLeft(
  showModal: ModalPresentation['showModal'],
  layout: TutorialOverlayLayout,
  margins: OverlayMargins,
  overlayWidth: number,
  targetRect?: TutorialTargetRect,
) {
  const centeredLeft = -margins.x + (overlayWidth - layout.cardWidth) / 2;
  if (showModal === 'centered' || layout.targetLeft === undefined || !targetRect) {
    return clamp(centeredLeft, layout.viewportBounds.left, layout.viewportBounds.right - layout.cardWidth);
  }

  const targetCenter = layout.targetLeft + targetRect.width / 2;
  return clamp(
    targetCenter - layout.cardWidth / 2,
    layout.viewportBounds.left,
    layout.viewportBounds.right - layout.cardWidth,
  );
}

function getModalTop(
  showModal: ModalPresentation['showModal'],
  layout: TutorialOverlayLayout,
  overlayHeight: number,
  margins: OverlayMargins,
  targetRect?: TutorialTargetRect,
) {
  if (showModal === 'no') return 0;
  if (showModal === 'centered') {
    return clamp(
      overlayHeight / 2 - layout.estimatedModalHeight / 2 - margins.y,
      layout.viewportBounds.top,
      layout.viewportBounds.bottom - layout.estimatedModalHeight,
    );
  }
  if (layout.targetTop === undefined || !targetRect) return 0;

  const unclampedTop =
    showModal === 'below'
      ? layout.targetTop + targetRect.height + layout.modalGap
      : layout.targetTop - layout.estimatedModalHeight - layout.modalGap;
  return clamp(unclampedTop, layout.viewportBounds.top, layout.viewportBounds.bottom - layout.estimatedModalHeight);
}

function getCompletedModalTop(
  showCompletedModal: ModalPresentation['showCompletedModal'],
  completedModalHeight: number,
  layout: TutorialOverlayLayout,
  overlayHeight: number,
  margins: OverlayMargins,
) {
  if (showCompletedModal === 'no') return 0;
  const height = completedModalHeight || 220;
  return clamp(
    overlayHeight / 2 - height / 2 - margins.y,
    layout.viewportBounds.top,
    layout.viewportBounds.bottom - height,
  );
}

function getHighlightRingStyle(
  targetRect: TutorialTargetRect | undefined,
  layout: TutorialOverlayLayout,
): StyleProp<ViewStyle> {
  if (layout.targetTop === undefined || layout.targetLeft === undefined || !targetRect) return { display: 'none' };

  return {
    position: 'absolute',
    zIndex: 11,
    left: layout.targetLeft - TARGET_RING_PADDING,
    top: layout.targetTop - TARGET_RING_PADDING,
    width: targetRect.width + TARGET_RING_PADDING * 2,
    height: targetRect.height + TARGET_RING_PADDING * 2,
    borderRadius: Math.min(
      theme.borderRadii['radius.xl'],
      Math.max(theme.borderRadii['radius.sm'], targetRect.height / 4),
    ),
    borderWidth: 2,
    borderColor: theme.colors['surface.tertiary'],
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 12,
    elevation: 5,
  };
}

function getPointerStyle({
  layout,
  modalHeight,
  modalTop,
  pointing,
  showModal,
  targetRect,
}: {
  layout: TutorialOverlayLayout;
  modalHeight: number;
  modalTop: number;
  pointing?: boolean;
  showModal: ModalPresentation['showModal'];
  targetRect?: TutorialTargetRect;
}): StyleProp<ViewStyle> {
  if (
    !pointing ||
    showModal === 'no' ||
    showModal === 'centered' ||
    layout.targetTop === undefined ||
    layout.targetLeft === undefined ||
    !targetRect
  ) {
    return { display: 'none' };
  }

  const targetCenter = layout.targetLeft + targetRect.width / 2;
  const cardHeight = modalHeight || layout.estimatedModalHeight;
  const targetStart = layout.targetTop - TARGET_RING_PADDING - POINTER_GAP;
  const targetEnd = layout.targetTop + targetRect.height + TARGET_RING_PADDING + POINTER_GAP;
  const pointerTop =
    showModal === 'below' ? targetEnd : Math.max(modalTop + cardHeight + POINTER_GAP, targetStart - POINTER_MAX_LENGTH);
  const pointerBottom =
    showModal === 'below' ? Math.min(modalTop - POINTER_GAP, targetEnd + POINTER_MAX_LENGTH) : targetStart;
  const pointerHeight = Math.max(0, pointerBottom - pointerTop);

  if (pointerHeight < POINTER_WIDTH) return { display: 'none' };

  return {
    position: 'absolute',
    zIndex: 12,
    left: targetCenter - POINTER_WIDTH / 2,
    top: pointerTop,
    width: POINTER_WIDTH,
    height: pointerHeight,
    borderRadius: POINTER_WIDTH / 2,
    backgroundColor: theme.colors['surface.tertiary'],
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.14,
    shadowRadius: 6,
    elevation: 4,
  };
}

interface TutorialTargetContext {
  activeTarget: TutorialTargetRef | undefined;
  currentRouteKey: string;
  isPointing: boolean;
  ownerRouteMatches: boolean;
}

interface TutorialTargetMeasurement extends TutorialTargetContext {
  rect: TutorialTargetRect | undefined;
}

function targetContextMatches(measurement: TutorialTargetMeasurement, context: TutorialTargetContext) {
  return (
    measurement.activeTarget === context.activeTarget &&
    measurement.currentRouteKey === context.currentRouteKey &&
    measurement.isPointing === context.isPointing &&
    measurement.ownerRouteMatches === context.ownerRouteMatches
  );
}

function useTutorialTargetMeasurement({
  context,
  measurementRequestIdRef,
  overlayHeight,
  overlayWidth,
  padding,
}: {
  context: TutorialTargetContext;
  measurementRequestIdRef: { current: number };
  overlayHeight: number;
  overlayWidth: number;
  padding?: TutorialOverlayPadding;
}) {
  const { activeTarget, currentRouteKey, isPointing, ownerRouteMatches } = context;
  const [targetMeasurement, setTargetMeasurement] = useState<TutorialTargetMeasurement>({
    ...context,
    rect: undefined,
  });
  let currentTargetMeasurement = targetMeasurement;

  if (!targetContextMatches(targetMeasurement, context)) {
    currentTargetMeasurement = { ...context, rect: undefined };
    setTargetMeasurement(currentTargetMeasurement);
  }

  const targetRect = context.isPointing && context.ownerRouteMatches ? currentTargetMeasurement.rect : undefined;
  const clearMeasuredTargetRect = () => {
    setTargetMeasurement((current) => {
      if (!targetContextMatches(current, context)) return current;
      return current.rect === undefined ? current : { ...current, rect: undefined };
    });
  };

  useEffect(() => {
    const requestId = measurementRequestIdRef.current + 1;
    measurementRequestIdRef.current = requestId;
    let animationFrame: number | undefined;
    let lastRect: TutorialTargetRect | undefined;
    let stableFrameCount = 0;
    const setMeasuredTargetRect = (nextValue: SetStateAction<TutorialTargetRect | undefined>) => {
      setTargetMeasurement((current) => {
        if (
          current.activeTarget !== activeTarget ||
          current.currentRouteKey !== currentRouteKey ||
          current.isPointing !== isPointing ||
          current.ownerRouteMatches !== ownerRouteMatches
        ) {
          return current;
        }
        const nextRect = typeof nextValue === 'function' ? nextValue(current.rect) : nextValue;
        return current.rect === nextRect ? current : { ...current, rect: nextRect };
      });
    };

    const scheduleMeasurement = (stable: boolean) => {
      const notBefore = Date.now() + (stable ? LIVE_MEASURE_INTERVAL_MS : 0);
      const measureWhenReady = () => {
        if (measurementRequestIdRef.current !== requestId) return;
        if (Date.now() < notBefore) {
          animationFrame = requestAnimationFrame(measureWhenReady);
          return;
        }
        measure();
      };
      animationFrame = requestAnimationFrame(measureWhenReady);
    };

    const measure = () => {
      if (measurementRequestIdRef.current !== requestId) return;
      const target = activeTarget?.current;
      if (!target) {
        setMeasuredTargetRect(undefined);
        scheduleMeasurement(true);
        return;
      }

      target.measureInWindow((x, y, width, height) => {
        if (measurementRequestIdRef.current !== requestId) return;

        const nextRect = addTutorialTargetPadding({ x, y, width, height }, padding);
        if (!isTutorialTargetInViewport(nextRect, overlayWidth, overlayHeight)) {
          lastRect = undefined;
          stableFrameCount = 0;
          setMeasuredTargetRect(undefined);
          scheduleMeasurement(true);
          return;
        }

        if (lastRect && areTutorialTargetRectsEqual(lastRect, nextRect)) {
          stableFrameCount += 1;
        } else {
          stableFrameCount = 0;
        }
        lastRect = nextRect;
        setMeasuredTargetRect((currentRect) =>
          currentRect && areTutorialTargetRectsEqual(currentRect, nextRect) ? currentRect : nextRect,
        );
        scheduleMeasurement(stableFrameCount >= STABLE_MEASURE_FRAME_COUNT);
      });
    };

    if (isPointing && ownerRouteMatches) scheduleMeasurement(false);
    return () => {
      measurementRequestIdRef.current += 1;
      if (animationFrame !== undefined) cancelAnimationFrame(animationFrame);
    };
  }, [
    activeTarget,
    currentRouteKey,
    isPointing,
    measurementRequestIdRef,
    overlayHeight,
    overlayWidth,
    ownerRouteMatches,
    padding,
  ]);

  return { clearMeasuredTargetRect, targetRect };
}

function stopHighlightPulse(highlightScale: SharedValue<number>) {
  cancelAnimation(highlightScale);
  highlightScale.set(1);
}

export const useTutorialOverlay = () => {
  const [tutorialOverlayOptions, setTutorialOverlayOptions] = useAtom(presentTutorialOverlayAtom);
  const sendHideOverlaySignal = useSetAtom(hideTutorialOverlayAtom);

  const showTutorialOverlay: ShowTutorialOverlayType = (
    tutorial: Tutorial,
    element?: TutorialTargetRef,
    title?: string,
    text?: string,
    showNextCta?: boolean,
    isLastStep?: boolean,
    padding?: TutorialOverlayPadding,
    targetRef?: TutorialRef,
    stepNumber?: number,
    totalSteps?: number,
    ownerRouteKey?: string,
  ) => {
    setTutorialOverlayOptions({
      tutorial,
      title,
      text,
      showNextCta,
      isLastStep,
      target: element,
      targetRef,
      padding,
      pointing: Boolean(element || targetRef),
      ownerRouteKey,
      stepNumber,
      totalSteps,
    });
  };

  const hideTutorialOverlay = async () => {
    if (!tutorialOverlayOptions) return;
    sendHideOverlaySignal(true);
    // Wait until the hiding animation completes before returning
    await delay(350);
  };

  return (() => {
    return {
      hideTutorialOverlay,
      showTutorialOverlay,
    };
  })();
};

export function useTutorialRef(elementName?: TutorialRef) {
  const viewRef = useRef<View>(null);
  const registrationId = useId();
  const isFocused = useIsFocused();
  const setTargetRegistrations = useSetAtom(tutorialTargetRegistrationsAtom);
  const { continueTutorial } = useTutorials();
  useEffect(() => {
    if (!elementName) return;

    setTargetRegistrations((registrations) => {
      const registrationsForTarget = registrations[elementName] ?? [];
      return {
        ...registrations,
        [elementName]: [
          ...registrationsForTarget.filter((registration) => registration.id !== registrationId),
          { id: registrationId, isFocused, ref: viewRef },
        ],
      };
    });

    return () => {
      setTargetRegistrations((registrations) => ({
        ...registrations,
        [elementName]: (registrations[elementName] ?? []).filter((registration) => registration.id !== registrationId),
      }));
    };
  }, [elementName, isFocused, registrationId, setTargetRegistrations]);
  return {
    continueTutorial,
    viewRef: elementName ? viewRef : undefined,
  };
}

export function useTutorials() {
  const navigation = navigationRef as unknown as SpecificNavigationProp;
  const { showTutorialOverlay, hideTutorialOverlay } = useTutorialOverlay();
  const tutorialsCompletedQuery = useOnePreference('tutorialsCompleted');
  const tutorialsCompletedPreference = tutorialsCompletedQuery.data?.value;
  const upsertPreference = useUpsertOnePreference('tutorialsCompleted');
  const [currentTutorialAt, setCurrentTutorial] = useAtom(currentTutorialAtom);
  const [currentTutorialStep, setCurrentTutorialStep] = useAtom(currentTutorialStepAtom);
  const [postponedSignals, setPostponedSignals] = useAtom(postponedSignalsAtom);
  const currentTutorialStartOptions = useAtomValue(currentTutorialStartOptionsAtom);
  const setCurrentTutorialStartOptions = useSetAtom(currentTutorialStartOptionsAtom);

  const tutorialsSteps = useTutorialDefinitions(showTutorialOverlay);
  const tutorialExecutionId = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const modalNavigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingTutorialStartRef = useRef<
    { tutorial: Tutorial; showModalFirst?: boolean; options?: StartTutorialOptions } | undefined
  >(undefined);
  const isBlockingModalVisibleHook = useIsBlockingModalVisible(currentTutorialStartOptions);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (modalNavigationTimeoutRef.current) clearTimeout(modalNavigationTimeoutRef.current);
    };
  }, []);

  const stopTutorial = (completed: boolean) => {
    const currentTutorial = getCurrentTutorial();
    if (!currentTutorial) return;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    tutorialExecutionId.current += 1;
    track(Event.TutorialEnded, { tutorial: currentTutorial, completed });
    setCurrentTutorial(undefined);
    setCurrentTutorialStep(0);
    setCurrentTutorialStartOptions(undefined);
    setPostponedSignals([]);
  };

  const completeTutorial = (completed: boolean, tutorial?: Tutorial) => {
    const resolvedTutorial = tutorial ?? getCurrentTutorial();
    if (!resolvedTutorial) return;

    if (!completed) {
      // User interrupted the tutorial; need to hide it
      void hideTutorialOverlay();
    }

    const didCompleteTutorial = !!tutorialsCompletedPreference?.find((x) => (x as Tutorial) === resolvedTutorial);
    if (!didCompleteTutorial) {
      const newValue = [...(tutorialsCompletedPreference ?? []), resolvedTutorial];
      upsertPreference.mutate(newValue);
    }

    stopTutorial(completed);
  };

  const continueTutorial = async (context?: Record<string, any>) => {
    const tutorial = (context?.currentTutorial as Tutorial | undefined) ?? getCurrentTutorial();
    if (!tutorial) return false;
    const steps = tutorialsSteps[tutorial];
    const stepIndex = typeof context?.stepIndex === 'number' ? context.stepIndex : currentTutorialStep;
    const tutorialStep = steps[stepIndex];
    const ownerRouteKey =
      typeof context?.ownerRouteKey === 'string' ? context.ownerRouteKey : getCurrentRouteBranchIdentity();

    if (stepIndex > 0 && context?.skipOverlayHide !== true) {
      await hideTutorialOverlay();
      if (getCurrentTutorial() !== tutorial) {
        return false;
      }
    }

    if (!tutorialStep) {
      completeTutorial(true);
      return true;
    }

    track(Event.TutorialStep, { tutorial, step: stepIndex + 1 });

    const startOptions = (context?.startOptions as StartTutorialOptions | undefined) ?? currentTutorialStartOptions;
    const executionContext = { ...context, currentTutorial: tutorial, stepIndex, startOptions, ownerRouteKey };
    const executionId = tutorialExecutionId.current;
    const stepDelay: number = typeof tutorialStep.delay === 'number' ? tutorialStep.delay : 0;

    const runStep = () => {
      timeoutRef.current = null;
      if (tutorialExecutionId.current !== executionId) return;
      if (getCurrentTutorial() !== tutorial) return;
      if (isBlockingModalVisible(startOptions)) {
        timeoutRef.current = setTimeout(runStep, 100);
        return;
      }

      tutorialStep.then(executionContext as any);
    };
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(runStep, stepDelay);

    setCurrentTutorialStep(stepIndex + 1);

    return true;
  };

  const goBackTutorial = async () => {
    const tutorial = getCurrentTutorial();
    if (!tutorial || currentTutorialStep <= 1) {
      return false;
    }

    await hideTutorialOverlay();
    if (getCurrentTutorial() !== tutorial) {
      return false;
    }

    return continueTutorial({
      stepIndex: currentTutorialStep - 2,
      currentTutorial: tutorial,
      skipOverlayHide: true,
    });
  };

  const startTutorialNow = (tutorial: Tutorial, showModalFirst?: boolean, options?: StartTutorialOptions) => {
    if (getCurrentTutorial() === tutorial) {
      return;
    }
    tutorialExecutionId.current += 1;
    setCurrentTutorialStartOptions(options);
    setCurrentTutorial(tutorial);
    setCurrentTutorialStep(0);
    setPostponedSignals([]);
    track(Event.TutorialStarted, { tutorial });

    if (showModalFirst) {
      if (modalNavigationTimeoutRef.current) clearTimeout(modalNavigationTimeoutRef.current);
      modalNavigationTimeoutRef.current = setTimeout(() => {
        modalNavigationTimeoutRef.current = null;
        navigation.navigate('tutorialModal', { tutorial }, { pop: true });
      }, 10);
    } else {
      void continueTutorial({ currentTutorial: tutorial, stepIndex: 0, startOptions: options });
    }
  };

  const startTutorial = (tutorial: Tutorial, showModalFirst?: boolean, options?: StartTutorialOptions) => {
    if (getCurrentTutorial() === tutorial) {
      return;
    }
    if (isBlockingModalVisible(options)) {
      pendingTutorialStartRef.current = { tutorial, showModalFirst, options };
      return;
    }
    pendingTutorialStartRef.current = undefined;
    startTutorialNow(tutorial, showModalFirst, options);
  };

  const resumePendingTutorial = useEffectEvent(() => {
    const pending = pendingTutorialStartRef.current;
    if (!pending) {
      return;
    }
    if (isBlockingModalVisible(pending.options)) {
      return;
    }
    pendingTutorialStartRef.current = undefined;
    startTutorialNow(pending.tutorial, pending.showModalFirst, pending.options);
  });
  useEffect(() => {
    resumePendingTutorial();
  }, [isBlockingModalVisibleHook]);

  const modalPausedTutorialRef = useRef(false);
  const synchronizeModalPause = useEffectEvent(() => {
    if (!currentTutorialAt) {
      modalPausedTutorialRef.current = false;
      return;
    }
    if (isBlockingModalVisibleHook) {
      if (!modalPausedTutorialRef.current) {
        modalPausedTutorialRef.current = true;
        void hideTutorialOverlay();
      }
      return;
    }
    if (modalPausedTutorialRef.current) {
      modalPausedTutorialRef.current = false;
      void continueTutorial({
        currentTutorial: currentTutorialAt,
        stepIndex: Math.max(0, currentTutorialStep - 1),
      });
    }
  });
  useEffect(() => {
    synchronizeModalPause();
  }, [currentTutorialAt, currentTutorialStep, isBlockingModalVisibleHook]);

  const hasCompletedTutorial = (tutorial: Tutorial) => {
    if (tutorialsCompletedQuery.isPending) return true;
    return !!tutorialsCompletedPreference?.find((x) => (x as Tutorial) === tutorial);
  };

  const startTutorialMaybe = (tutorial: Tutorial) => {
    if (!hasCompletedTutorial(tutorial)) {
      startTutorial(tutorial);
      return true;
    }
    return false;
  };

  const postponeSignal = (signal: Signal) => {
    const newValue = [...postponedSignals, signal];
    setPostponedSignals(newValue);
  };

  return (() => {
    return {
      hasCompletedTutorial,
      completeTutorial,
      startTutorialMaybe,
      startTutorial,
      continueTutorial,
      goBackTutorial,
      stopTutorial,
      postponeSignal,
    };
  })();
}

type TutorialTranslation = ReturnType<typeof useTranslation>['t'];
type TutorialAnimatedStyle = ReturnType<typeof useAnimatedStyle>;

function getTutorialCtaText(tutorialOverlayOptions: TutorialOverlayProps, t: TutorialTranslation) {
  const ctaTextKey = tutorialOverlayOptions.text!.replace('.text', '.cta');
  const translatedCta = t(ctaTextKey);
  if (translatedCta !== ctaTextKey) return translatedCta;
  return tutorialOverlayOptions.isLastStep ? t('finish') : t('next');
}

function getTutorialStepProgress(tutorialOverlayOptions: TutorialOverlayProps) {
  const stepNumber = tutorialOverlayOptions.stepNumber ?? 0;
  const totalSteps = tutorialOverlayOptions.totalSteps ?? 0;
  return {
    isMultiStep: stepNumber > 0 && totalSteps > 1,
    stepNumber,
    totalSteps,
  };
}

function TutorialBackdrop({
  highlightAnimatedStyle,
  highlightRingStyle,
  margins,
  modalAnimatedStyle,
  overlayAnimatedStyle,
  overlayHeight,
  overlayStyle,
  overlayWidth,
  pointerStyle,
  showTouchShield,
}: {
  highlightAnimatedStyle: TutorialAnimatedStyle;
  highlightRingStyle: StyleProp<ViewStyle>;
  margins: OverlayMargins;
  modalAnimatedStyle: TutorialAnimatedStyle;
  overlayAnimatedStyle: TutorialAnimatedStyle;
  overlayHeight: number;
  overlayStyle: (position: OverlaySegment) => StyleProp<ViewStyle>;
  overlayWidth: number;
  pointerStyle: StyleProp<ViewStyle>;
  showTouchShield?: boolean;
}) {
  return (
    <>
      <Animated.View style={[overlayStyle('top'), overlayAnimatedStyle]} />
      <Animated.View style={[overlayStyle('left'), overlayAnimatedStyle]} />
      <Animated.View style={[overlayStyle('right'), overlayAnimatedStyle]} />
      <Animated.View style={[overlayStyle('bottom'), overlayAnimatedStyle]} />
      {showTouchShield ? (
        <View
          testID="tutorial-overlay-touch-shield"
          style={{
            position: 'absolute',
            left: -margins.x,
            top: -margins.y,
            width: overlayWidth,
            height: overlayHeight,
            zIndex: 11,
          }}
        />
      ) : null}
      <Animated.View pointerEvents="none" style={[highlightRingStyle, highlightAnimatedStyle]} />
      <Animated.View pointerEvents="none" style={[pointerStyle, modalAnimatedStyle]} />
    </>
  );
}

function TutorialStepModal({
  cardWidth,
  ctaText,
  deviceWidth,
  isMultiStep,
  modalAnimatedStyle,
  modalLeft,
  modalTop,
  onBack,
  onClose,
  onContinue,
  onLayout,
  showModal,
  stepNumber,
  t,
  totalSteps,
  tutorialOverlayOptions,
}: {
  cardWidth: number;
  ctaText: string;
  deviceWidth: number;
  isMultiStep: boolean;
  modalAnimatedStyle: TutorialAnimatedStyle;
  modalLeft: number;
  modalTop: number;
  onBack: () => void;
  onClose: () => void;
  onContinue: () => void;
  onLayout: (event: LayoutChangeEvent) => void;
  showModal: ModalPresentation['showModal'];
  stepNumber: number;
  t: TutorialTranslation;
  totalSteps: number;
  tutorialOverlayOptions: TutorialOverlayProps;
}) {
  return (
    <Animated.View
      onLayout={onLayout}
      style={[
        {
          display: showModal === 'no' ? 'none' : 'flex',
          zIndex: 12,
          backgroundColor: 'white',
          borderRadius: theme.borderRadii['radius.lg'],
          borderColor: theme.colors['surface.tertiary'],
          borderWidth: 4,
          padding: deviceWidth <= 360 ? theme.spacing['spacing.general.x5'] : theme.spacing['spacing.general.x6'],
          position: 'absolute',
          width: cardWidth,
          top: modalTop,
          left: modalLeft,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.16,
          shadowRadius: 20,
          elevation: 8,
        },
        modalAnimatedStyle,
      ]}
    >
      {tutorialOverlayOptions.isLastStep && tutorialOverlayOptions.showNextCta ? null : (
        <View
          style={{
            position: 'absolute',
            zIndex: 13,
            top: theme.spacing['spacing.general.x1'],
            right: theme.spacing['spacing.general.x1'],
          }}
        >
          <Button accessibilityLabel={t('close', { ns: 'common' })} flex={0} onPress={onClose}>
            <IconX strokeWidth={ICON_STROKE_WIDTH} />
          </Button>
        </View>
      )}

      <Column
        alignItems="center"
        gap="spacing.general.x5"
        style={{ paddingTop: theme.spacing['spacing.general.x1'], width: '100%' }}
      >
        <Text fontSize={20} fontWeight="700" textAlign="center" variant="bodyBase">
          {tutorialOverlayOptions.title ? t(tutorialOverlayOptions.title) : ''}
        </Text>
        <Text fontSize={16} textAlign="center" variant="bodyBase">
          <Trans
            components={{ strong: <Text fontWeight="700" /> }}
            i18nKey={tutorialOverlayOptions.text}
            ns="tutorial"
          />
        </Text>
        {isMultiStep ? (
          <Row alignItems="center" justifyContent="space-between" marginTop="spacing.general.x1" width="100%">
            <Text color="text.secondary" fontSize={14} variant="bodyBase">
              {t('stepIndicator', { current: stepNumber, total: totalSteps })}
            </Text>
            <Row alignItems="center" gap="spacing.general.x2">
              {stepNumber > 1 ? (
                <Button
                  onPress={onBack}
                  style={{ borderRadius: 999, paddingHorizontal: theme.spacing['spacing.general.x5'] }}
                  variant="whiteWithBorder"
                >
                  <Text fontWeight="700">{t('back')}</Text>
                </Button>
              ) : null}
              {tutorialOverlayOptions.showNextCta ? (
                <Button
                  onPress={onContinue}
                  style={{
                    borderRadius: 999,
                    minWidth: 100,
                    paddingHorizontal: theme.spacing['spacing.general.x5'],
                  }}
                  variant="primary"
                >
                  {ctaText}
                </Button>
              ) : null}
            </Row>
          </Row>
        ) : (
          <Button
            onPress={onContinue}
            style={{ display: tutorialOverlayOptions.showNextCta ? 'flex' : 'none', minWidth: 140 }}
            variant="primary"
          >
            {ctaText}
          </Button>
        )}
      </Column>
    </Animated.View>
  );
}

function TutorialCompletedModal({
  cardWidth,
  modalAnimatedStyle,
  modalLeft,
  modalTop,
  onContinue,
  onLayout,
  showCompletedModal,
  t,
  tutorialOverlayOptions,
}: {
  cardWidth: number;
  modalAnimatedStyle: TutorialAnimatedStyle;
  modalLeft: number;
  modalTop: number;
  onContinue: () => void;
  onLayout: (event: LayoutChangeEvent) => void;
  showCompletedModal: ModalPresentation['showCompletedModal'];
  t: TutorialTranslation;
  tutorialOverlayOptions: TutorialOverlayProps;
}) {
  return (
    <Animated.View
      onLayout={onLayout}
      style={[
        {
          display: showCompletedModal === 'no' ? 'none' : 'flex',
          zIndex: 12,
          backgroundColor: 'white',
          borderRadius: theme.borderRadii['radius.lg'],
          borderColor: theme.colors['surface.tertiary'],
          borderWidth: 4,
          padding: theme.spacing['spacing.general.x6'],
          position: 'absolute',
          width: cardWidth,
          top: modalTop,
          left: modalLeft,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.16,
          shadowRadius: 20,
          elevation: 8,
        },
        modalAnimatedStyle,
      ]}
    >
      <View style={{ marginBottom: theme.spacing['spacing.general.x6'] }}>
        <Text fontSize={20} fontWeight="bold" textAlign="center" variant="bodyBase">
          {tutorialOverlayOptions.title ? t(tutorialOverlayOptions.title) : ''}
        </Text>
      </View>

      <Column
        alignItems="center"
        gap="spacing.general.x6"
        style={{ marginBottom: theme.spacing['spacing.general.x6'] }}
      >
        <FancySeparator width={cardWidth - 2 * theme.spacing['spacing.general.x6']} />
        <Row alignItems="center">
          {showCompletedModal === Tutorial.PublishOffers ? (
            <View style={{ paddingTop: 0, width: 60, marginRight: 16 }}>
              <Image
                source={images.badge.checked[UserLevel.CONTRIBUTOR]}
                style={{ flex: 1, width: 60, maxHeight: 60 }}
              />
            </View>
          ) : null}

          <Text flex={1} fontSize={16} variant="bodyBase">
            <Trans
              components={{ strong: <Text fontWeight="700" /> }}
              i18nKey={tutorialOverlayOptions.text}
              ns="tutorial"
            />
          </Text>
        </Row>

        <FancySeparator width={cardWidth - 2 * theme.spacing['spacing.general.x6']} />
      </Column>

      <Column>
        <Button onPress={onContinue} variant="primary">
          {t('levelUpModal.secondaryButtonText', { ns: 'one-listing' })}
        </Button>
      </Column>
    </Animated.View>
  );
}

function useTutorialOverlayAnimationStyles({
  highlightScaleAnimated,
  modalOpacityAnimated,
  modalTranslateYAnimated,
  overlayOpacityAnimated,
}: {
  highlightScaleAnimated: SharedValue<number>;
  modalOpacityAnimated: SharedValue<number>;
  modalTranslateYAnimated: SharedValue<number>;
  overlayOpacityAnimated: SharedValue<number>;
}) {
  const overlayAnimatedStyle = useAnimatedStyle(() => ({ opacity: overlayOpacityAnimated.get() }));
  const modalAnimatedStyle = useAnimatedStyle(() => ({
    opacity: modalOpacityAnimated.get(),
    transform: [{ translateY: modalTranslateYAnimated.get() }],
  }));
  const highlightAnimatedStyle = useAnimatedStyle(() => ({
    opacity: modalOpacityAnimated.get(),
    transform: [{ scale: highlightScaleAnimated.get() }],
  }));

  return { highlightAnimatedStyle, modalAnimatedStyle, overlayAnimatedStyle };
}

export function TutorialOverlay() {
  const { t } = useTranslation('tutorial');
  const deviceWidth = useDeviceWidth();
  const windowDims = useWindowDimensions();
  const overlayWidth = windowDims.width;
  const overlayHeight = windowDims.height;
  const insets = useSafeAreaInsets();
  const [tutorialOverlayOptions, setTutorialOverlayOptions] = useAtom(presentTutorialOverlayAtom);
  const [hideTutorialOverlaySignal, sendHideOverlaySignal] = useAtom(hideTutorialOverlayAtom);
  const targetRegistrations = useAtomValue(tutorialTargetRegistrationsAtom);
  const currentRouteKey = useSyncExternalStore(subscribeNavigationState, getCurrentRouteBranchIdentity, () => '');
  const { continueTutorial, completeTutorial, goBackTutorial } = useTutorials();

  const [margins, setMargins] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [modalPresentation, setModalPresentation] = useState<{
    showModal: TutorialOverlayPlacement | 'no';
    showCompletedModal: Tutorial | 'no';
  }>({ showModal: 'no', showCompletedModal: 'no' });
  const [modalHeight, setModalHeight] = useState(0);
  const [completedModalHeight, setCompletedModalHeight] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isRendered, setIsRendered] = useState(false);
  const [isExplicitlyHiding, setIsExplicitlyHiding] = useState(false);
  const container = useRef<View>(null);
  const measurementRequestId = useRef(0);
  const overlayOpacityAnimated = useSharedValue(0);
  const modalOpacityAnimated = useSharedValue(0);
  const modalTranslateYAnimated = useSharedValue(8);
  const highlightScaleAnimated = useSharedValue(1);
  const { highlightAnimatedStyle, modalAnimatedStyle, overlayAnimatedStyle } = useTutorialOverlayAnimationStyles({
    highlightScaleAnimated,
    modalOpacityAnimated,
    modalTranslateYAnimated,
    overlayOpacityAnimated,
  });

  const ownerRouteMatches = isTutorialOverlayRouteActive(tutorialOverlayOptions, currentRouteKey);
  const registeredTarget = getFocusedTutorialTarget(targetRegistrations, tutorialOverlayOptions?.targetRef);
  const activeTarget = tutorialOverlayOptions?.target ?? registeredTarget;
  const isPointing = Boolean(tutorialOverlayOptions?.pointing);
  const { clearMeasuredTargetRect, targetRect } = useTutorialTargetMeasurement({
    context: { activeTarget, currentRouteKey, isPointing, ownerRouteMatches },
    measurementRequestIdRef: measurementRequestId,
    overlayHeight,
    overlayWidth,
    padding: tutorialOverlayOptions?.padding,
  });

  const canPresent = canPresentTutorialOverlay(tutorialOverlayOptions, currentRouteKey, targetRect);

  const layout = getTutorialOverlayLayout({
    deviceWidth,
    insets,
    margins,
    modalHeight,
    overlayHeight,
    overlayWidth,
    targetRect,
  });
  const { cardWidth } = layout;

  const onContainerLayout = () => {
    if (!container.current) return;
    container.current.measureInWindow((x, y) => {
      setMargins({ x, y });
    });
  };

  const onModalLayout = (event: LayoutChangeEvent) => {
    setModalHeight(event.nativeEvent.layout.height);
  };

  const onCompletedModalLayout = (event: LayoutChangeEvent) => {
    setCompletedModalHeight(event.nativeEvent.layout.height);
  };

  const overlayStyle = (position: OverlaySegment) =>
    getOverlaySegmentStyle(position, targetRect, overlayWidth, overlayHeight, margins);
  const currentModalPresentation = getModalPresentation({
    isRendered,
    layout,
    previous: modalPresentation,
    targetRect,
    tutorialOverlayOptions,
  });
  if (
    modalPresentation.showModal !== currentModalPresentation.showModal ||
    modalPresentation.showCompletedModal !== currentModalPresentation.showCompletedModal
  ) {
    setModalPresentation(currentModalPresentation);
  }
  const { showCompletedModal, showModal } = currentModalPresentation;
  const modalLeft = () => getModalLeft(showModal, layout, margins, overlayWidth, targetRect);
  const modalTop = () => getModalTop(showModal, layout, overlayHeight, margins, targetRect);
  const completedModalTop = () =>
    getCompletedModalTop(showCompletedModal, completedModalHeight, layout, overlayHeight, margins);
  const highlightRingStyle = getHighlightRingStyle(targetRect, layout);
  const pointerStyle = getPointerStyle({
    layout,
    modalHeight,
    modalTop: modalTop(),
    pointing: tutorialOverlayOptions?.pointing,
    showModal,
    targetRect,
  });

  const animateOpacity = (animatedValue: SharedValue<number>, toValue: number, onComplete?: () => void) => {
    animatedValue.set(
      withTiming(toValue, { duration: OVERLAY_FADE_DURATION_MS, easing: Easing.out(Easing.quad) }, (finished) => {
        if (finished && onComplete) {
          scheduleOnRN(onComplete);
        }
      }),
    );
  };

  const startHighlightPulse = () => {
    stopHighlightPulse(highlightScaleAnimated);
    if (!targetRect) return;

    highlightScaleAnimated.set(
      withRepeat(
        withSequence(
          withTiming(1.025, { duration: 850, easing: Easing.inOut(Easing.quad) }),
          withTiming(1, { duration: 850, easing: Easing.inOut(Easing.quad) }),
        ),
        -1,
      ),
    );
  };

  const animateModalIn = () => {
    cancelAnimation(modalOpacityAnimated);
    cancelAnimation(modalTranslateYAnimated);
    modalOpacityAnimated.set(0);
    modalTranslateYAnimated.set(8);
    modalOpacityAnimated.set(
      withDelay(40, withTiming(1, { duration: MODAL_ANIMATION_DURATION_MS, easing: Easing.out(Easing.cubic) })),
    );
    modalTranslateYAnimated.set(
      withDelay(40, withTiming(0, { duration: MODAL_ANIMATION_DURATION_MS, easing: Easing.out(Easing.cubic) })),
    );
  };

  const animateModalOut = (onComplete?: () => void) => {
    modalOpacityAnimated.set(withTiming(0, { duration: MODAL_ANIMATION_DURATION_MS, easing: Easing.in(Easing.quad) }));
    modalTranslateYAnimated.set(
      withTiming(6, { duration: MODAL_ANIMATION_DURATION_MS, easing: Easing.in(Easing.quad) }, (finished) => {
        if (finished && onComplete) {
          scheduleOnRN(onComplete);
        }
      }),
    );
  };

  const presentOverlay = useEffectEvent(() => {
    setIsRendered(true);
    setIsVisible(true);
    animateOpacity(overlayOpacityAnimated, 0.62);
    animateModalIn();
    startHighlightPulse();
  });

  const dismissOverlay = useEffectEvent(() => {
    setIsVisible(false);
    stopHighlightPulse(highlightScaleAnimated);
    animateOpacity(overlayOpacityAnimated, 0);
    animateModalOut(() => setIsRendered(false));
  });

  const handleHideOverlaySignal = useEffectEvent(() => {
    sendHideOverlaySignal(false);
    measurementRequestId.current += 1;
    setIsExplicitlyHiding(true);

    if (!isRendered) {
      clearMeasuredTargetRect();
      setTutorialOverlayOptions(undefined);
      setIsExplicitlyHiding(false);
      return;
    }

    setIsVisible(false);
    stopHighlightPulse(highlightScaleAnimated);
    animateOpacity(overlayOpacityAnimated, 0);
    animateModalOut(() => {
      setIsRendered(false);
      clearMeasuredTargetRect();
      setTutorialOverlayOptions(undefined);
      setIsExplicitlyHiding(false);
    });
  });

  useEffect(() => {
    return () => {
      stopHighlightPulse(highlightScaleAnimated);
      cancelAnimation(overlayOpacityAnimated);
      cancelAnimation(modalOpacityAnimated);
      cancelAnimation(modalTranslateYAnimated);
      setTutorialOverlayOptions(undefined);
    };
  }, [
    modalOpacityAnimated,
    modalTranslateYAnimated,
    overlayOpacityAnimated,
    setTutorialOverlayOptions,
    highlightScaleAnimated,
  ]);

  useEffect(() => {
    if (canPresent) {
      if (!shouldResumeTutorialOverlay(canPresent, isVisible, isExplicitlyHiding)) return;
      const animationFrame = requestAnimationFrame(() => {
        presentOverlay();
      });
      return () => cancelAnimationFrame(animationFrame);
    }

    if (!isRendered || !isVisible) return;

    const animationFrame = requestAnimationFrame(() => {
      dismissOverlay();
    });
    return () => cancelAnimationFrame(animationFrame);
  }, [canPresent, isExplicitlyHiding, isRendered, isVisible]);

  useEffect(() => {
    if (!hideTutorialOverlaySignal) return;
    const animationFrame = requestAnimationFrame(() => {
      handleHideOverlaySignal();
    });
    return () => cancelAnimationFrame(animationFrame);
  }, [hideTutorialOverlaySignal]);

  if (!tutorialOverlayOptions || !isRendered) return null;

  const ctaText = getTutorialCtaText(tutorialOverlayOptions, t);
  const { isMultiStep, stepNumber, totalSteps } = getTutorialStepProgress(tutorialOverlayOptions);

  return (
    <View
      collapsable={false}
      onLayout={onContainerLayout}
      ref={container}
      style={{
        pointerEvents: 'box-none',
        position: 'absolute',
        left: 0,
        top: 0,
        width: overlayWidth,
        height: overlayHeight,
        zIndex: !isMobileNative ? 10000 : 1000,
      }}
    >
      <TutorialBackdrop
        highlightAnimatedStyle={highlightAnimatedStyle}
        highlightRingStyle={highlightRingStyle}
        margins={margins}
        modalAnimatedStyle={modalAnimatedStyle}
        overlayAnimatedStyle={overlayAnimatedStyle}
        overlayHeight={overlayHeight}
        overlayStyle={overlayStyle}
        overlayWidth={overlayWidth}
        pointerStyle={pointerStyle}
        showTouchShield={tutorialOverlayOptions.showNextCta}
      />
      <TutorialStepModal
        cardWidth={cardWidth}
        ctaText={ctaText}
        deviceWidth={deviceWidth}
        isMultiStep={isMultiStep}
        modalAnimatedStyle={modalAnimatedStyle}
        modalLeft={modalLeft()}
        modalTop={modalTop()}
        onBack={() => void goBackTutorial()}
        onClose={() => completeTutorial(false)}
        onContinue={() => void continueTutorial()}
        onLayout={onModalLayout}
        showModal={showModal}
        stepNumber={stepNumber}
        t={t}
        totalSteps={totalSteps}
        tutorialOverlayOptions={tutorialOverlayOptions}
      />
      <TutorialCompletedModal
        cardWidth={cardWidth}
        modalAnimatedStyle={modalAnimatedStyle}
        modalLeft={modalLeft()}
        modalTop={completedModalTop()}
        onContinue={() => void continueTutorial()}
        onLayout={onCompletedModalLayout}
        showCompletedModal={showCompletedModal}
        t={t}
        tutorialOverlayOptions={tutorialOverlayOptions}
      />
    </View>
  );
}
